import React from "react";

/**
 * Continuous horizontal marquee of the 10 legacy portraits.
 *
 * Renders a single row that scrolls right-to-left forever, plus a
 * duplicate row inline so the loop is seamless (when the first copy
 * exits, the second copy is already in position). Pauses on hover so
 * visitors can look closer. Placed once on the landing page just below
 * the SponsoredStrip.
 *
 * Every image is an AI caricature served through the on-disk cached
 * /api/img proxy — no copyrighted photographs.
 */

const LEGACY = [
  { key: "chanakya",    name: "Acharya Chanakya",    era: "c. 375 BCE", tag: "Statecraft · Arthashastra",       seed: 3711,
    prompt: "warm watercolor caricature portrait, ancient Indian sage Chanakya, wise bearded acharya with a shikha tuft, saffron dhoti and shawl, holding a palm leaf manuscript, gentle smile, folk-art painting style, creamy paper background, no text, no watermark" },
  { key: "aryabhatta",  name: "Aryabhatta",          era: "476 CE",     tag: "Astronomer · Discovered ZERO",    seed: 4763,
    prompt: "colorful sketch caricature portrait of Aryabhatta the ancient Indian astronomer and mathematician, young bald scholar with a small shikha tuft, red vertical tilak on forehead, saffron ochre robe with beaded rudraksha mala, one hand holding up a big bright yellow ZERO 0 symbol he discovered, other hand touching planet saturn with rings, colorful planets sun and moon orbiting around him in blue swirling cosmos, floating handwritten mathematical equations and Sanskrit numerals in the background, warm cream paper background, hand-drawn crayon and ink style, no photo-realistic style, no text signature, no watermark" },
  { key: "dnyaneshwar", name: "Sant Dnyaneshwar",    era: "1275 CE",    tag: "Bhakti Saint · Dnyaneshwari",     seed: 1275,
    prompt: "warm watercolor caricature portrait, young Sant Dnyaneshwar cross-legged, peaceful smiling boy-saint holding an open Marathi manuscript, soft golden halo, saffron robe, gentle folk-art painting style, creamy paper background, no text, no watermark" },
  { key: "eknath",      name: "Sant Eknath",         era: "1533 CE",    tag: "Poet-Saint · Bhagavata",          seed: 1535,
    prompt: "vintage Marathi devotional illustration of young Sant Eknath the bhakti poet-saint of Paithan, seated cross-legged on a low wooden asan stool, bald head with small shikha tuft and thick black moustache, red vertical tilak on forehead, bare torso with yellow saffron angavastram draped over one shoulder, tulsi rudraksha mala around neck, white dhoti wrapped at waist, holding an open palm-leaf manuscript pothi and reading it thoughtfully, another closed pothi on a red cloth beside him, misty greenish brown outdoor background, traditional hand-painted Indian folk-art style, warm sepia tones, no text, no watermark, no signature" },
  { key: "ramdas",      name: "Samarth Ramdas",      era: "1608 CE",    tag: "Guru · Dasbodh",                  seed: 1608,
    prompt: "warm watercolor caricature portrait, Samarth Ramdas swami, kind bearded Marathi guru holding a wooden staff and manuscript, ochre saffron robe, gentle smile, folk-art painting style, creamy paper background, no text, no watermark" },
  { key: "bajirao",     name: "Bajirao Peshwa I",    era: "1720 CE",    tag: "Undefeated Maratha General",      seed: 1720,
    prompt: "warm watercolor caricature portrait, Bajirao Peshwa the first, young confident Maratha commander with curled mustache, ivory pearl-studded pagdi turban, saffron angarkha, gentle smile, folk-art illustration, creamy paper background, no text, no watermark" },
  { key: "lakshmibai",  name: "Rani Lakshmibai",     era: "1828 CE",    tag: "Warrior Queen of Jhansi",         seed: 1828,
    prompt: "vintage indian mural style caricature portrait of Rani Lakshmibai the queen of Jhansi, brave young Maratha warrior queen on horseback, wearing green nine-yard warrior saree with royal jewellery, sword drawn high, baby Damodar Rao tied to her back with cloth, determined proud expression, saffron and red battle backdrop with the Jhansi fort silhouette, folk-art painting style, no text, no watermark, no signature" },
  { key: "tilak",       name: "Lokmanya Tilak",      era: "1856 CE",    tag: "Freedom Fighter · Kesari",        seed: 1857,
    prompt: "vintage portrait illustration of Lokmanya Bal Gangadhar Tilak the Indian freedom fighter, elder Marathi scholar with a full thick white handlebar moustache, wearing a signature red Puneri pheta turban tied high, small red vertical bindi tilak on forehead, dressed in a plain white dhoti kurta with an Indian tricolor saffron white and green uparne shawl draped diagonally across chest, calm dignified expression looking straight ahead, painted brushstroke Indian tricolor flag saffron white and green as background, hand-drawn illustration style, warm cream paper, no text, no watermark, no signature" },
  { key: "anandibai",   name: "Anandi Gopal Joshi",  era: "1865 CE",    tag: "India&rsquo;s First Woman Doctor", seed: 1865,
    prompt: "warm watercolor caricature portrait, Anandi Gopal Joshi first Indian woman doctor, gentle young Maharashtrian lady in a nine-yard saree with graduation stole, kind smile, folk-art illustration, creamy paper background, no text, no watermark" },
  { key: "savarkar",    name: "Veer Savarkar",       era: "1883 CE",    tag: "Revolutionary · Author",          seed: 1883,
    prompt: "vintage indian mural style caricature portrait of Swatantryaveer Vinayak Damodar Savarkar, young Marathi freedom fighter and revolutionary writer, clean moustache, wearing simple white kurta with a fountain pen tucked in pocket, thoughtful sharp gaze, tricolor Indian freedom-struggle backdrop with a manuscript scroll, folk-art painting style, warm cream paper background, no text, no watermark, no signature" },
];

function imgUrl(seed, prompt, size = 360) {
  const params = new URLSearchParams({ prompt, seed: String(seed), w: String(size), h: String(size) });
  return `/api/img?${params.toString()}`;
}

function LegacyChip({ item }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-2 shrink-0"
      data-testid={`legacy-chip-${item.key}`}
    >
      <div className="relative rounded-full ring-2 ring-primary/40 ring-offset-2 ring-offset-background overflow-hidden shadow-sm w-14 h-14 sm:w-16 sm:h-16 shrink-0">
        <img
          src={imgUrl(item.seed, item.prompt, 360)}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="leading-tight text-left">
        <div className="text-[13px] sm:text-sm font-semibold text-foreground whitespace-nowrap">
          {item.name}
        </div>
        <div className="text-[9px] uppercase tracking-[0.16em] text-primary mt-0.5 whitespace-nowrap">
          {item.era}
        </div>
        <div
          className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap"
          dangerouslySetInnerHTML={{ __html: item.tag }}
        />
      </div>
    </div>
  );
}

/**
 * Marquee — one continuous strip. Two identical rows in a wider container
 * animated with a CSS keyframe that translates -50% on the X axis, giving
 * a seamless loop. `pause-on-hover` group class stops the animation when
 * the user hovers so they can read a specific card.
 */
export default function LegacyMarquee() {
  return (
    <section
      className="relative border-b border-border/60 bg-gradient-to-r from-primary/[0.06] via-transparent to-accent/[0.06] overflow-hidden"
      data-testid="legacy-marquee"
    >
      {/* Fade masks on the edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent z-10" />

      <div className="mx-auto max-w-full py-3 flex items-center">
        <span className="hidden sm:inline-flex shrink-0 px-4 text-[10px] uppercase tracking-[0.24em] text-primary font-semibold">
          Illustrious Legacy
        </span>

        <div className="group flex-1 overflow-hidden">
          <div
            className="flex w-max animate-legacy-marquee group-hover:[animation-play-state:paused]"
            data-testid="legacy-marquee-track"
          >
            {LEGACY.map((it) => <LegacyChip key={`a-${it.key}`} item={it} />)}
            {/* Duplicate row for a seamless loop */}
            {LEGACY.map((it) => <LegacyChip key={`b-${it.key}`} item={it} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
